<template>
  <ModalDialog
    ref="dialog"
    title="MYOB Configuration"
    icon="fa-cog"
    :submitting="submitting"
    submitTitle="Update"
    sizeClass="modal-xl"
    :submitDisabled="false"
    :scrollable="true"
    @closed="onClosed"
    @submitted="onSubmitted">

    <FormGroup v-bind:form="myobForm" :labelColumnSize="3">
      <LoadingPanel ref="loadingPanel" @update="loading = $event" />
      <TabbedBox v-if="!loading">
        <template v-slot:tablist>
          <Tab tabid="tab-companyfile" :first="true">
            <i class="far fa-briefcase"></i> Company File
          </Tab>
          <Tab tabid="tab-config">
            <i class="far fa-cog"></i> Sync Config
          </Tab>
          <Tab tabid="tab-accounts" v-if="isCompanyFileConfigured">
            <i class="far fa-money-check-alt"></i> Accounts
          </Tab>
          <Tab tabid="tab-taxrates" v-if="isCompanyFileConfigured">
            <i class="far fa-donate"></i> Tax Rates
          </Tab>
          <Tab tabid="tab-syncerrors" :errorCount="syncErrorList.length" v-if="isCompanyFileConfigured">
            <i class="far fa-exclamation-triangle"></i> Sync Errors
          </Tab>
        </template>

        <template v-slot:tabcontent>
          <TabPage :first="true" tabid="tab-companyfile">
            <ContentTitle>Company File</ContentTitle>

            <FormRow label="Company File" :labelColumnSize="3" :helpTip="isCompanyFileConfigured ? 'Disconnect and reconnect MYOB from Synergy to change the company file' : ''">
              <FormStaticLabel v-if="!isCompanyFileConfigured">Error: Company file is not configured. Please reconnect MYOB from Synergy to set up the company file.</FormStaticLabel>
              <FormStaticLabel v-if="isCompanyFileConfigured">{{myobCompanyFile.name}}</FormStaticLabel>
            </FormRow>
          </TabPage>

          <TabPage tabid="tab-config">
            <ContentTitle>MYOB Sync Direction Configuration</ContentTitle>
            <NotificationBar type="info">
              <NotificationBarTitle>MYOB Syncing</NotificationBarTitle>
              <ul>
                <li>Objects in Synergy are considered the <strong>source of truth</strong>, meaning all data and updates should originate and be modified within Synergy where possible.</li>
                <li>Only payment allocations to invoices within MYOB will be imported from MYOB into the Synergy system</li>
                <li>Syncing will not occur until the accounts &amp; tax rates have been linked to MYOB accounts &amp; tax rates</li>
              </ul>
            </NotificationBar>

            <StaticDataTable class="static-table-list">
              <template v-slot:header>
                <tr>
                  <th width="40%">Setting</th>
                  <th width="20%">Read From MYOB</th>
                  <th width="20%">Write To MYOB</th>
                  <th width="20%"><Tooltip helpStyle label="Items in Synergy or MYOB that have a creation or update date earlier than this will not be synced">Sync No Earlier Than</Tooltip></th>
                </tr>
              </template>
              <template v-slot:body>
                <tr>
                  <td>Contacts<br />
                  <small>Note: Contacts <strong>must</strong> be synced with MYOB to sync other items below</small></td>
                  <td>-</td>
                  <td><FormSwitch :booleanMode="true" v-model="myobOptions[MyobOptionTypes.contactWriteSyncEnabled]" /></td>
                  <td><FormDate v-model="myobOptions[MyobOptionTypes.contactSyncStartDate]" /></td>
                </tr>
                <tr>
                  <td>Invoices<br />
                  <small>Note: Invoices <strong>must</strong> be synced with Myob to sync payment allocations</small></td>
                  <td>-</td>
                  <td><FormSwitch :booleanMode="true" v-model="myobOptions[MyobOptionTypes.invoiceWriteSyncEnabled]" /></td>
                  <td><FormDate v-model="myobOptions[MyobOptionTypes.invoiceSyncStartDate]" /></td>
                </tr>
                <tr>
                  <td>Credit Notes</td>
                  <td>-</td>
                  <td><FormSwitch :booleanMode="true" v-model="myobOptions[MyobOptionTypes.creditNoteWriteSyncEnabled]" /></td>
                  <td><FormDate v-model="myobOptions[MyobOptionTypes.creditNoteSyncStartDate]" /></td>
                </tr>
                <tr>
                  <td>Payments</td>
                  <td><FormSwitch :booleanMode="true" v-model="myobOptions[MyobOptionTypes.invoicePaymentReadSyncEnabled]" /></td>
                  <td><FormSwitch :booleanMode="true" v-model="myobOptions[MyobOptionTypes.invoicePaymentWriteSyncEnabled]" /></td>
                  <td><FormDate v-model="myobOptions[MyobOptionTypes.invoicePaymentSyncStartDate]" /></td>
                </tr>
                <tr>
                  <td>Overpayments</td>
                  <td>-</td>
                  <td><FormSwitch :booleanMode="true" v-model="myobOptions[MyobOptionTypes.overpaymentWriteSyncEnabled]" /></td>
                  <td><FormDate v-model="myobOptions[MyobOptionTypes.overpaymentSyncStartDate]" /></td>
                </tr>
              </template>
            </StaticDataTable>            
          </TabPage>

          

          <TabPage tabid="tab-accounts" v-if="isCompanyFileConfigured">
            <ContentTitle>Myob Accounts Setup</ContentTitle>
            <FormRow v-for="accountConfig of accountConfigList" :key="accountConfig.accountType" :label="accountConfig.name" :labelColumnSize="3">
              <FormSearchableSelect v-model="accountConfig.myobAccountUID" :options="myobAccountLookup" :appendToBody="true" />
            </FormRow>
          </TabPage>

          <TabPage tabid="tab-taxrates" v-if="isCompanyFileConfigured">
            <ContentTitle>Tax Rates Setup</ContentTitle>
            <FormRow v-for="taxRate of taxRateConfigList" :key="taxRate.taxRateId" :label="taxRate.name" :labelColumnSize="3" :fieldCount="2">
              <template v-slot:field1>
                <FormSearchableSelect v-model="taxRate.myobTaxCodeUID" :options="myobTaxCodeLookup" :appendToBody="true" />
              </template>
              <template v-slot:field2>
                <FormCheckbox v-model="taxRate.usePaymentsSync" note="Use for the payments syncing" />
              </template>
            </FormRow>
          </TabPage>

          <TabPage tabid="tab-syncerrors" v-if="isCompanyFileConfigured">
            <ContentTitle>Sync Error Message List</ContentTitle>

            <StaticDataTable class="static-table-list">
              <template v-slot:header>
                <tr>
                  <th width="10%">Type</th>
                  <th width="15%">Name</th>
                  <th width="50%">Message</th>
                  <th width="15%">Date</th>
                  <th width="10%">Action</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="syncError of syncErrorList" :key="`${syncError.linkType}-${syncError.linkId}`">
                  <template v-if="syncError.linkType == LinkTypes.contact">
                    <td>Contact</td>
                    <td><LinkContact :id="syncError.linkId" :name="syncError.name" /></td>
                  </template>
                  <template v-if="syncError.linkType == LinkTypes.invoice">
                    <td>Invoice</td>
                    <td><LinkInvoice :id="syncError.linkId" :name="syncError.name" /></td>
                  </template>
                  <template v-if="syncError.linkType == LinkTypes.overPayment">
                    <td>Over Payment</td>
                    <td><LinkOverPayment :id="syncError.linkId" :name="syncError.name" /></td>
                  </template>
                  <template v-if="syncError.linkType == LinkTypes.paymentReceived">
                    <td>Payment Received</td>
                    <td><LinkPaymentReceived :id="syncError.linkId" :name="syncError.name" /></td>
                  </template>
                  <template v-if="syncError.linkType == LinkTypes.creditNote">
                    <td>Credit Note</td>
                    <td><LinkCreditNote :id="syncError.linkId" :name="syncError.name" /></td>
                  </template>
                  <td>{{syncError.message}}</td>
                  <td>{{formatDateTime(syncError.syncErrorDate)}}</td>
                  <td>
                    <ActionDropMenu :tableAction="true" :forceDropdown="true">
                      <DropdownMenuItem v-if="syncError.isMessageClearable" icon="fa-times" @click="clearSyncError(syncError)">Clear Message</DropdownMenuItem>
                    </ActionDropMenu>
                  </td>
                </tr>
              </template>
              <template v-slot:empty v-if="syncErrorList.length == 0">
                No sync errors in the system
              </template>
            </StaticDataTable>
            
          </TabPage>
        </template>
      </TabbedBox>
      
    </FormGroup>
  </ModalDialog>
</template>

<style>
</style>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import modalDialog from '@/mixins/modalDialog'
import IntegrationMyobAPIService from '@/integrations/myob/services/apiService'

import { MyobOptionTypes } from '@/integrations/myob/types'
import { LinkTypes } from '@/store/types'

export default {
  name: 'MyobConfigDialog',
  mixins: [
    modalDialog,
    form
  ],
  components: {
    ModalDialog,
  },
  props: {
    
  },
  data() {
    return {
      myobForm: {},
      loading: true,
      submitting: false,
      myobOptions: {},
      isCompanyFileConfigured: false,
      myobCompanyFile: {
        id: null,
        name: ''
      },
      myobAccountsList: [],
      myobNestedAccountsList: [],
      myobTaxCodesList: [],

      taxRateConfigList: [],
      accountConfigList: [],

      syncErrorList: []
    }
  },
  created() {
    this.MyobOptionTypes = MyobOptionTypes
    this.LinkTypes = LinkTypes
  },
  mounted() {
  },
  computed: {
    myobAccountLookup() {
      return this.myobNestedAccountsList
    },
    myobTaxCodeLookup() {
      return _.concat(
        { name: 'None', value: null },
        this.myobTaxCodesList.map(myobTaxCode => {
          return { name: myobTaxCode.name, value: myobTaxCode.id }
        })
      )
    }
  },
  methods: {
    show(integrationData) {
      // Reset form
      this.myobForm = {}
      this.submitting = false

      this.requestData()

      return modalDialog.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      this.submitting = true
      this.myobForm = this.clearFormResponse()

      var updateData = {
        options: this.myobOptions,
        accountList: this.accountConfigList,
        taxRateList: this.taxRateConfigList
      }

      var myobService = new IntegrationMyobAPIService(this.$api)
      myobService.updateConfig(updateData)
      .then(response => {
        this.modalClosed(true)
        this.hide()
      })
      .catch(err => {
        this.$data.myobForm = this.processFormResponse(err)
      })
      .finally(() => {
        this.submitting = false
      })
    },
    reloadData() {
      this.requestData()
    },
    reloadSyncErrorList() {
      var myobService = new IntegrationMyobAPIService(this.$api)
      myobService.getSyncErrorList()
      .then(response => {
        this.syncErrorList = response.data
      })
      .catch(err => {
        this.displayNetworkError(err, 'Error loading sync error list')
      })
    },
    requestData() {
      var myobService = new IntegrationMyobAPIService(this.$api)

      this.$refs.loadingPanel.startLoading()
      Promise.all([
        myobService.getConfig(),
        myobService.getMyobTaxCodeList(),
        myobService.getTaxRateConfigList(),
        myobService.getMyobAccountList(),
        myobService.getAccountConfigList(),
        myobService.getSyncErrorList()
      ]).then(results => {
        this.myobOptions = results[0].data.options || {}
        this.myobCompanyFile = results[0].data.companyFile || {}

        // Company file is now pre-set during connection phase from 2026 onwards
        this.isCompanyFileConfigured = this.myobCompanyFile.id != null

        this.myobTaxCodesList = results[1].data
        this.taxRateConfigList = results[2].data

        this.myobAccountsList = results[3].data
        this.accountConfigList = results[4].data

        this.syncErrorList = results[5].data

        // Build the nested accounts list
        this.buildNestedAccountList()
      })
      .catch(err => {
        this.$refs.loadingPanel.setError('Error accessing Myob configuration data', err)
      })
      .finally(() => {
        this.$refs.loadingPanel.finishedLoading()
      })
    },
    clearSyncError(syncError) {
      var myobService = new IntegrationMyobAPIService(this.$api)

      this.confirmDeleteMessage('Clear Sync Message?', 'This will clear the message from appearing in the sync error list. This will not remove, update or resync any data')
      .then(confirm => {
        if(confirm) {
          myobService.clearSyncErrorMessage(syncError)
          .then(response => {
            this.successToast('Message Cleared')
            this.reloadSyncErrorList()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error clearing message')
          })
        }
      })
    },
    buildNestedAccountList() {
      this.getMyobAccountsList(this.myobNestedAccountsList)
    },
    getMyobAccountsList(list, parent = null, parentName = '') {

      this.myobAccountsList
      .filter(accountInfo => accountInfo.parentId == (parent ? parent.id : null))
      .forEach(accountInfo => {
        let name = `${parentName}${parentName ? ' - ' : ''}${accountInfo.name}`

        if(!accountInfo.isHeader) {
          list.push({
            value: accountInfo.id,
            name: name
          })
        } else {
          this.getMyobAccountsList(list, accountInfo, name)
        }
      })
    }
  }
}
</script>